import React from 'react';
import { Box, Text, Heading, SimpleGrid, VStack } from '@chakra-ui/react';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface BillingCardProps {
  sectionData: BinderSectionData;
}

const BillingCard: React.FC<BillingCardProps> = ({ sectionData }) => {
  const { data } = sectionData;
  const { billing = {}, method_of_payment = {}, paymentData } = data || {};
  const colors = useBinderCardColors();

  // Create billing info array with field mappings
  const billingInfo = [
    // Row 1: Payment Plan
    {
      field: 'Payment Plan',
      value: method_of_payment.type_of_payment_plan || 'N/A',
    },

    // Row 2: Payment Amount, Payment Date
    {
      field: 'Payment Amount',
      value: method_of_payment.total_estimated_cost,
    },
    {
      field: 'Payment Date',
      value: billing.payment_date || 'N/A',
    },

    // Row 3: Account details (regular)
    { field: 'Account Name', value: paymentData.account_holder_name },
    { field: 'Account Number', value: '•••••••••••••••' },
    { field: 'Bank Number', value: paymentData.bank_nr },
    { field: 'Transit Number', value: paymentData.routing_number },

    // Row 4: Credit Card details (regular)
    { field: 'Credit Card Name', value: 'N/A' }, // Not found in data
    { field: 'Credit Card Number', value: 'N/A' }, // Not found in data
    { field: 'Expiry', value: 'N/A' },
    { field: 'CVV', value: 'N/A' }, // Not found in data

    // Row 5: Downpayment details (red text)
    {
      field: 'Downpayment Amount',
      value: billing.down_payment_amount,
      isRed: true,
    },
    {
      field: 'Downpayment Date',
      value: billing.down_payment_date,
      isRed: true,
    },

    // Row 6: Account details (red text)
    // {
    //   field: 'Account Name',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
    // {
    //   field: 'Account Number',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
    // {
    //   field: 'Bank Number',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
    // {
    //   field: 'Transit Number',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },

    // Row 7: Credit Card details (red text)
    // {
    //   field: 'Credit Card Name',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
    // {
    //   field: 'Credit Card Number',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
    // {
    //   field: 'Expiry',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
    // {
    //   field: 'CVV',
    //   value: 'N/A', // Not found in data
    //   isRed: true,
    //   isDownpayment: true,
    // },
  ];

  // Handle case when no billing info
  if (!billing || Object.keys(billing).length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No billing information available
        </Text>
      </Box>
    );
  }

  // Separate fields into different layout groups
  // Row 1: 1-column - Payment Plan
  const oneColumnFields = billingInfo.filter(
    (item: any) => item.field === 'Payment Plan',
  );

  // Row 2: 2-column - Payment Amount, Payment Date
  const twoColumnFields1 = billingInfo.filter(
    (item: any) =>
      item.field === 'Payment Amount' || item.field === 'Payment Date',
  );

  // Row 3: 4-column - Account details (regular)
  const fourColumnFields1 = billingInfo.filter(
    (item: any) =>
      (item.field === 'Account Name' ||
        item.field === 'Account Number' ||
        item.field === 'Bank Number' ||
        item.field === 'Transit Number') &&
      !item.isDownpayment,
  );

  // Row 4: 4-column - Credit Card details (regular)
  const fourColumnFields2 = billingInfo.filter(
    (item: any) =>
      (item.field === 'Credit Card Name' ||
        item.field === 'Credit Card Number' ||
        item.field === 'Expiry' ||
        item.field === 'CVV') &&
      !item.isDownpayment,
  );

  // Row 5: 2-column - Downpayment Amount, DownpaymentDate (red)
  const twoColumnFields2 = billingInfo.filter(
    (item: any) =>
      item.field === 'Downpayment Amount' || item.field === 'Downpayment Date',
  );

  // Row 6: 4-column - Account details (red)
  // const fourColumnFields3 = billingInfo.filter(
  //   (item: any) =>
  //     (item.field === 'Account Name' ||
  //       item.field === 'Account Number' ||
  //       item.field === 'Bank Number' ||
  //       item.field === 'Transit Number') &&
  //     item.isDownpayment,
  // );

  // Row 7: 4-column - Credit Card details (red)
  // const fourColumnFields4 = billingInfo.filter(
  //   (item: any) =>
  //     (item.field === 'Credit Card Name' ||
  //       item.field === 'Credit Card Number' ||
  //       item.field === 'Expiry' ||
  //       item.field === 'CVV') &&
  //     item.isDownpayment,
  // );

  const renderField = (item: any) => {
    return (
      <VStack align="start" spacing={2}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={item.isRed ? colors.failedTextColor : colors.labelColor}
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {item.field}
        </Text>
        <Box>
          {item.value === 'N/A' ? (
            <Text color="gray.400" fontStyle="italic" fontSize="sm">
              -
            </Text>
          ) : (
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={item.isRed ? colors.failedTextColor : colors.textColor}
              wordBreak="break-word"
            >
              {item.value}
            </Text>
          )}
        </Box>
      </VStack>
    );
  };

  return (
    <Box>
      <Box mb={4}>
        <Heading size="sm" color={colors.textColor} mb={2}>
          Billing
        </Heading>
      </Box>

      <Box
        borderRadius="md"
        borderWidth="1px"
        borderColor={colors.borderColor}
        overflow="hidden"
      >
        {/* Row 1: 1-column - Payment Plan */}
        {oneColumnFields.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={1} spacing={0}>
              {oneColumnFields.map((item: any, index: number) => (
                <Box
                  key={`one-col-${index}`}
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 2: 2-column - Payment Amount, Payment Date */}
        {twoColumnFields1.length > 0 && (
          <Box bg="transparent">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
              {twoColumnFields1.map((item: any, index: number) => (
                <Box
                  key={`two-col-1-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index === 0 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 3: 4-column - Account details (regular) */}
        {fourColumnFields1.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
              {fourColumnFields1.map((item: any, index: number) => (
                <Box
                  key={`four-col-1-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 4 !== 3 ? '1px' : 0,
                    lg: index % 4 !== 3 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 4: 4-column - Credit Card details (regular) */}
        {fourColumnFields2.length > 0 && (
          <Box bg="transparent">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
              {fourColumnFields2.map((item: any, index: number) => (
                <Box
                  key={`four-col-2-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 4 !== 3 ? '1px' : 0,
                    lg: index % 4 !== 3 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 5: 2-column - Downpayment Amount, DownpaymentDate (red) */}
        {twoColumnFields2.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
              {twoColumnFields2.map((item: any, index: number) => (
                <Box
                  key={`two-col-2-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index === 0 ? '1px' : 0,
                  }}
                  // borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 6: 4-column - Account details (red) */}
        {/* {fourColumnFields3.length > 0 && (
          <Box bg="transparent">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
              {fourColumnFields3.map((item: any, index: number) => (
                <Box
                  key={`four-col-3-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 4 !== 3 ? '1px' : 0,
                    lg: index % 4 !== 3 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )} */}

        {/* Row 7: 4-column - Credit Card details (red) */}
        {/* {fourColumnFields4.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
              {fourColumnFields4.map((item: any, index: number) => (
                <Box
                  key={`four-col-4-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 4 !== 3 ? '1px' : 0,
                    lg: index % 4 !== 3 ? '1px' : 0,
                  }}
                  borderBottomWidth={
                    index >= fourColumnFields4.length - 4 ? 0 : '1px'
                  }
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )} */}
      </Box>
    </Box>
  );
};

export default BillingCard;
